//----------------------------------------------------------------------------------------------------------------------
// Collider Debug Manager
//
// Wraps BabylonJS PhysicsViewer to provide per-node collider debug visualization with color support.
//----------------------------------------------------------------------------------------------------------------------

import { type AbstractMesh, Color3, StandardMaterial, type TransformNode } from '@babylonjs/core';
import { PhysicsViewer } from '@babylonjs/core/Debug/physicsViewer';

import type { Disposable } from '../interfaces/lifecycle.ts';

//----------------------------------------------------------------------------------------------------------------------

export type ColliderDebugConfig = boolean | string | Record<string, string | false>;

//----------------------------------------------------------------------------------------------------------------------

interface ShownEntry
{
    debugMesh : AbstractMesh;
    colorMaterial ?: StandardMaterial;
}

//----------------------------------------------------------------------------------------------------------------------

export class ColliderDebugManager implements Disposable
{
    private _viewer : PhysicsViewer | null = null;
    private _shown = new Map<TransformNode, ShownEntry>();
    private _materials = new Map<string, StandardMaterial>();

    //------------------------------------------------------------------------------------------------------------------
    // Public API
    //------------------------------------------------------------------------------------------------------------------

    showColliderForNode(node : TransformNode, color ?: string) : void
    {
        if(!node.physicsBody || this._shown.has(node))
        {
            return;
        }

        const viewer = this._getViewer(node);
        const debugMesh = viewer.showBody(node.physicsBody);

        if(!debugMesh)
        {
            return;
        }

        const entry : ShownEntry = { debugMesh };

        if(color)
        {
            const material = this._getColorMaterial(color, node);
            debugMesh.material = material;
            entry.colorMaterial = material;
        }

        this._shown.set(node, entry);
    }

    hideColliderForNode(node : TransformNode) : void
    {
        const entry = this._shown.get(node);
        if(!entry || !this._viewer)
        {
            return;
        }

        if(node.physicsBody)
        {
            this._viewer.hideBody(node.physicsBody);
        }

        this._shown.delete(node);
    }

    hideAll() : void
    {
        for(const [ node ] of this._shown)
        {
            this.hideColliderForNode(node);
        }
    }

    isShown(node : TransformNode) : boolean
    {
        return this._shown.has(node);
    }

    async $teardown() : Promise<void>
    {
        this.dispose();
    }

    dispose() : void
    {
        if(this._viewer)
        {
            this.hideAll();
            this._viewer.dispose();
            this._viewer = null;
        }

        this._shown.clear();

        for(const mat of this._materials.values())
        {
            mat.dispose();
        }

        this._materials.clear();
    }

    //------------------------------------------------------------------------------------------------------------------
    // Private
    //------------------------------------------------------------------------------------------------------------------

    private _getViewer(node : TransformNode) : PhysicsViewer
    {
        if(!this._viewer)
        {
            this._viewer = new PhysicsViewer(node.getScene());
        }

        return this._viewer;
    }

    private _getColorMaterial(color : string, node : TransformNode) : StandardMaterial
    {
        let material = this._materials.get(color);
        if(material)
        {
            return material;
        }

        material = new StandardMaterial(`sage-collider-debug-${ color }`, node.getScene());
        material.emissiveColor = Color3.FromHexString(color);
        material.disableLighting = true;
        material.wireframe = true;
        this._materials.set(color, material);

        return material;
    }
}

//----------------------------------------------------------------------------------------------------------------------
